import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles } from '@/utils/pageBranding'
import {
    Users,
    Search,
    MoreVertical,
    Eye,
    Edit,
    Building,
    Shield,
    Calendar,
    ArrowLeft,
    Plus,
    ChevronRight,
    Loader2,
    AlertCircle,
    ChevronLeft,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { adminService } from '@/services/adminService'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '@/hooks/useAuth'
import { getVisibleRoles, canManageUsers } from '@/utils/auth'
import { useAuditLog } from '@/utils/auditLogger'

interface User {
    id: number
    email: string
    full_name: string
    role: string
    role_display: string
    is_active: boolean
    is_verified: boolean
    date_joined: string
    company_name?: string
    phone_number?: string
    merchant?: {
        id: number
        company_name: string
        email: string
        status: string
    }
    submerchant?: {
        id: number
        name: string
        status: string
    }
    sale_shops_count: number
    last_login_display: string
}

export default function AdminUserList() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const navigate = useNavigate()
    const { user: authUser } = useAuth()
    const { logUserAction, logDataAccess } = useAuditLog()
    const [users, setUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [roleFilter, setRoleFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [hasNext, setHasNext] = useState(false)
    const [hasPrevious, setHasPrevious] = useState(false)
    const [error, setError] = useState('')

    const pageSize = 20

    // Helper function to safely format dates
    const formatDateSafely = (dateString: string): string => {
        try {
            const date = new Date(dateString)
            if (isNaN(date.getTime())) {
                return 'Invalid date'
            }
            return formatDistanceToNow(date, { addSuffix: true })
        } catch (error) {
            return 'Invalid date'
        }
    }

    useEffect(() => {
        fetchUsers()
    }, [currentPage, roleFilter, statusFilter])

    // Filter users based on current user's permissions
    const filterUsersByPermissions = (users: User[]): User[] => {
        if (!authUser) return []

        // Platform admins can see all users
        if (canManageUsers(authUser)) {
            return users
        }

        // Merchant admins can only see users from their merchant
        if (authUser?.role === 'merchant_admin' && authUser.merchant) {
            return users.filter(u =>
                u.merchant?.id === authUser.merchant ||
                u.role === 'merchant_admin' ||
                u.role === 'merchant_user'
            )
        }

        // Submerchant admins can only see users from their submerchant
        if (authUser?.role === 'submerchant_admin' && authUser.submerchant) {
            return users.filter(u =>
                u.submerchant?.id === authUser.submerchant ||
                u.role === 'submerchant_admin' ||
                u.role === 'submerchant_user'
            )
        }

        // Other users can't see any users
        return []
    }

    const fetchUsers = async () => {
        setLoading(true)
        setError('')
        try {
            const params: any = {
                page: currentPage,
                page_size: pageSize,
            }

            if (searchQuery.trim()) {
                params.search = searchQuery.trim()
            }
            if (roleFilter) {
                params.role = roleFilter
            }
            if (statusFilter) {
                if (statusFilter === 'active') {
                    params.is_active = true
                } else if (statusFilter === 'inactive') {
                    params.is_active = false
                } else if (statusFilter === 'verified') {
                    params.is_verified = true
                } else if (statusFilter === 'unverified') {
                    params.is_verified = false
                }
            }

            const response = await adminService.getUsers(params)

            if (response.success && response.data) {
                const allUsers = response.data.results as any
                const filteredUsers = filterUsersByPermissions(allUsers)

                // Log data access
                logDataAccess(authUser, 'user_list', undefined, true, 'Successfully fetched user list', {
                    totalUsers: allUsers.length,
                    filteredUsers: filteredUsers.length,
                    filters: { searchQuery, roleFilter, statusFilter }
                })

                setUsers(filteredUsers)
                setTotalCount(filteredUsers.length) // Update count to reflect filtered results
                setHasNext(!!response.data.next)
                setHasPrevious(!!response.data.previous)
            } else {
                // Log failed data access
                logDataAccess(authUser, 'user_list', undefined, false, 'Failed to fetch user list')
                setError('Failed to fetch users')
            }
        } catch (err: any) {
            setError(adminService.handleError(err))
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        setCurrentPage(1)
        fetchUsers()
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'admin':
            case 'superuser':
                return <Shield className="w-4 h-4" />
            case 'merchant_admin':
            case 'merchant_user':
                return <Building className="w-4 h-4" />
            default:
                return <Users className="w-4 h-4" />
        }
    }

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'admin':
            case 'superuser':
                return 'bg-red-100 text-red-800 border-red-200'
            case 'merchant_admin':
                return 'bg-blue-100 text-blue-800 border-blue-200'
            case 'merchant_user':
                return 'bg-blue-50 text-blue-700 border-blue-100'
            case 'submerchant_admin':
                return 'bg-green-100 text-green-800 border-green-200'
            case 'submerchant_user':
                return 'bg-green-50 text-green-700 border-green-100'
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200'
        }
    }

    const getStatusColor = (isActive: boolean, isVerified: boolean) => {
        if (!isActive) return 'bg-red-100 text-red-800'
        if (!isVerified) return 'bg-yellow-100 text-yellow-800'
        return 'bg-green-100 text-green-800'
    }

    const getStatusText = (isActive: boolean, isVerified: boolean) => {
        if (!isActive) return 'Inactive'
        if (!isVerified) return 'Unverified'
        return 'Active'
    }

    const totalPages = Math.ceil(totalCount / pageSize)

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="relative z-10 p-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/dashboard')}
                                className="p-2 rounded-lg hover:opacity-80"
                                style={{ color: branding.textSecondaryColor }}
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <div>
                                <div className="flex items-center space-x-3 mb-2">
                                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                        <Users className="w-6 h-6" style={{ color: branding.secondaryColor }} />
                                    </div>
                                    <h1 className="text-4xl font-bold" style={pageStyles.text}>User Management</h1>
                                </div>
                                <p className="text-lg" style={pageStyles.textSecondary}>Manage users, merchants, and submerchants</p>
                            </div>
                        </div>
                        {canManageUsers(authUser) && (
                            <Button
                                onClick={() => {
                                    logUserAction(authUser, 'create_user_form', 'user', undefined, {
                                        action: 'navigate_to_create_form'
                                    })
                                    navigate('/admin/users/create')
                                }}
                                className="px-6 py-3 hover:opacity-80"
                                style={pageStyles.primaryBg}
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                Create User
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="mb-6">
                    <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {/* Search */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Search Users
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <Input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Search by name, email, or company..."
                                        className="pl-10 bg-gray-700/50 border-gray-600 text-white placeholder-gray-400 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                            </div>

                            {/* Role Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Role
                                </label>
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                >
                                    <option value="">All Roles</option>
                                    {getVisibleRoles(authUser).map(role => (
                                        <option key={role} value={role}>
                                            {role === 'admin' ? 'Platform Admin' :
                                                role === 'merchant_admin' ? 'Merchant Admin' :
                                                    role === 'merchant_user' ? 'Merchant User' :
                                                        role === 'submerchant_admin' ? 'Submerchant Admin' :
                                                            role === 'submerchant_user' ? 'Submerchant User' :
                                                                role === 'auditor' ? 'Auditor' : role}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Status Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Status
                                </label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                >
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="verified">Verified</option>
                                    <option value="unverified">Unverified</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end mt-4">
                            <Button
                                onClick={handleSearch}
                                className="px-6 py-2 text-white bg-orange-500 hover:bg-orange-600"
                            >
                                <Search className="w-4 h-4 mr-2" />
                                Search
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <p className="text-red-300">{error}</p>
                    </div>
                )}

                {/* Users Table */}
                <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                            <span className="ml-3 text-gray-300">Loading users...</span>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Users className="w-12 h-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-semibold text-gray-300 mb-2">No users found</h3>
                            <p className="text-gray-400 text-center mb-4">
                                {searchQuery || roleFilter || statusFilter
                                    ? 'Try adjusting your search criteria'
                                    : 'Get started by creating your first user'}
                            </p>
                            {canManageUsers(authUser) && (
                                <Button
                                    onClick={() => navigate('/admin/users/create')}
                                    className="px-6 py-3 text-white bg-orange-500 hover:bg-orange-600"
                                >
                                    <Plus className="w-5 h-5 mr-2" />
                                    Create User
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Table Header */}
                            <div className="bg-gray-700/50 px-6 py-4 border-b border-gray-600">
                                <div className="grid grid-cols-12 gap-4 text-sm font-semibold text-gray-300">
                                    <div className="col-span-3">User</div>
                                    <div className="col-span-2">Role</div>
                                    <div className="col-span-2">Merchant/Submerchant</div>
                                    <div className="col-span-1">Status</div>
                                    <div className="col-span-2">Created</div>
                                    <div className="col-span-1">Sales</div>
                                    <div className="col-span-1">Actions</div>
                                </div>
                            </div>

                            {/* Table Body */}
                            <div className="divide-y divide-gray-600">
                                {users.map((user) => (
                                    <div key={user.id} className="px-6 py-4 hover:bg-gray-700/30 transition-colors">
                                        <div className="grid grid-cols-12 gap-4 items-center">
                                            {/* User Info */}
                                            <div className="col-span-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold">
                                                        {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-white">{user.full_name}</p>
                                                        <p className="text-sm text-gray-400">{user.email}</p>
                                                        {user.company_name && (
                                                            <p className="text-xs text-gray-500">{user.company_name}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Role */}
                                            <div className="col-span-2">
                                                <div className="flex items-center space-x-2">
                                                    {getRoleIcon(user.role)}
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(user.role)}`}>
                                                        {user.role_display}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Merchant/Submerchant */}
                                            <div className="col-span-2">
                                                {user.merchant ? (
                                                    <div className="flex items-center space-x-2">
                                                        <Building className="w-4 h-4 text-blue-400" />
                                                        <div>
                                                            <p className="text-sm font-medium text-white">{user.merchant.company_name}</p>
                                                            <p className="text-xs text-gray-400">{user.merchant.email}</p>
                                                        </div>
                                                    </div>
                                                ) : user.submerchant ? (
                                                    <div className="flex items-center space-x-2">
                                                        <Users className="w-4 h-4 text-green-400" />
                                                        <div>
                                                            <p className="text-sm font-medium text-white">{user.submerchant.name}</p>
                                                            <p className="text-xs text-gray-400">Submerchant</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">No association</span>
                                                )}
                                            </div>

                                            {/* Status */}
                                            <div className="col-span-1">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.is_active, user.is_verified)}`}>
                                                    {getStatusText(user.is_active, user.is_verified)}
                                                </span>
                                            </div>

                                            {/* Created */}
                                            <div className="col-span-2">
                                                <div className="flex items-center space-x-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-300">
                                                        {formatDateSafely(user.date_joined)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Sales Count */}
                                            <div className="col-span-1">
                                                <span className="text-sm text-gray-300">{user.sale_shops_count}</span>
                                            </div>

                                            {/* Actions */}
                                            <div className="col-span-1">
                                                <div className="flex items-center space-x-2">
                                                    {/* View button - visible to all authenticated users */}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            logUserAction(authUser, 'view_user', 'user', user.id, {
                                                                userEmail: user.email,
                                                                userRole: user.role
                                                            })
                                                            navigate(`/admin/users/${user.id}`)
                                                        }}
                                                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>

                                                    {/* Edit button - only visible to users who can manage users */}
                                                    {canManageUsers(authUser) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                logUserAction(authUser, 'edit_user', 'user', user.id, {
                                                                    userEmail: user.email,
                                                                    userRole: user.role
                                                                })
                                                                navigate(`/admin/users/${user.id}/edit`)
                                                            }}
                                                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                    )}

                                                    {/* More actions - only visible to users who can manage users */}
                                                    {canManageUsers(authUser) && (
                                                        <div className="relative">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700"
                                                            >
                                                                <MoreVertical className="w-4 h-4" />
                                                            </Button>
                                                            {/* Action dropdown would go here */}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="bg-gray-700/50 px-6 py-4 border-t border-gray-600">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-300">
                                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                variant="ghost"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={!hasPrevious || loading}
                                                className="px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-600"
                                            >
                                                <ChevronLeft className="w-4 h-4 mr-1" />
                                                Previous
                                            </Button>
                                            <span className="px-3 py-2 text-sm text-gray-300">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <Button
                                                variant="ghost"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={!hasNext || loading}
                                                className="px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-600"
                                            >
                                                Next
                                                <ChevronRight className="w-4 h-4 ml-1" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
